
import { mapGetters } from 'vuex'
import { COPY } from './copy'
import { newSchedulerState, newPerson, eligiblePeople, buildBookBody, fillFromRecord, newClientAllowedMethods, referredByLabel, showClientState, OTHER_STATE, payerFor, agentTakesTier, restoreSchedulerState, draftSchedulerState, draftRecord } from './logic'
import ClientStatusCard from './ClientStatusCard.vue'
import ClientInfoCard from './ClientInfoCard.vue'
import AttendeesCard from './AttendeesCard.vue'
import SoaSection from './SoaSection.vue'
import MethodTimeCard from './MethodTimeCard.vue'
import SummaryCard from './SummaryCard.vue'
import { fetchOpenStates, isOpenState, stateNames } from '~/util/open-states'
import { releaseHoldOnLeave, tabSessionId } from '~/util/hold-release'
import { clearDraft, loadDraft, saveDraft } from '~/util/scheduler-draft'

export default {
    components: { ClientStatusCard, ClientInfoCard, AttendeesCard, SoaSection, MethodTimeCard, SummaryCard },
    props: {
        // Resolved by the page (schedule/index.vue fetch): ?id preload + ?agent pin.
        preloadedClient: { type: Object, default: null },
        preloadedSpouse: { type: Object, default: null },
        pinnedAgent: { type: Object, default: null },
    },
    data () {
        return {
            COPY,
            state: newSchedulerState(),
            spouse: null,
            agents: [],
            // #727: the open states (FL/RI), from /open-states/.
            openStates: [],
            booking: false,
            heldUntil: null,
            // clientId -> soonest upcoming Acuity appointment (or null once
            // checked). Powers the "already has a consultation" notice (#576).
            upcomingByClientId: {},
            // Allowed consult methods for a RETURNING primary, from the backend
            // (paying-source authority). null = not looked up yet. New clients
            // are decided client-side from their referral (newClientAllowedMethods).
            returningAllowedMethods: null,
            // #679 polish: the endpoint's authoritative enrolled boolean,
            // fetched with the methods — drives ClientStatusCard's Enrolled
            // badge instead of inferring it from survey-only + returning.
            returningEnrolled: null,
            // The returning primary is a former Benefit Bay client (closed
            // 2026-10-01): /client-allowed-methods/ says so alongside its empty
            // method list, and the blocked card shows the partnership message.
            returningFormerIchra: false,
            // #727: /client-allowed-methods/ says the open-state rule governs this
            // returning client (Other source, FL/RI, no other entitlement).
            returningOpenStateOnly: false,
            // Payer / non-payer routing: the client's tier from /client-allowed-methods/
            // (null until it answers, or when it cannot).
            returningPayer: null,
            agentDefaulted: false,
            advisorTouched: false,
            // #679 polish: group+aep combos whose new-client no-service block
            // was already reported to the backend this session (see the
            // primaryBlocked watcher) — never spam the log on re-renders.
            noServiceLogged: {},
            // Per tab, kept across reloads (util/hold-release.js): the `sadm-`
            // prefix is how the backend tells a scheduler session from a client.
            sessionId: tabSessionId('sadm-'),
            // The advisor has entered something (Taylor Vinton, 2026-10-29: a
            // tab that left /schedule mid-call came back empty). While true the
            // form is kept in the tab (util/scheduler-draft.js) and leaving the
            // page asks first; a booking, Start over or signing out ends it.
            touched: false,
            // When the draft this page picked back up was saved, and whether
            // it had a time picked that had to go.
            restoredAt: null,
            restoredSlot: false,
        }
    },
    computed: {
        ...mapGetters(['isAEPScheduling']),
        // Everything a reload would lose that the advisor put in.
        draftData () {
            return { state: this.state, spouse: this.spouse, agentDefaulted: this.agentDefaulted, advisorTouched: this.advisorTouched }
        },
        restoredAtLabel () {
            return this.restoredAt ? this.$moment(this.restoredAt).format('h:mm A') : ''
        },
        // The primary's allowed methods: from the backend for a returning client,
        // computed from the referral for a new one. null = undetermined.
        allowedMethods () {
            const p = this.state.primary
            if (p.returning === true) return this.returningAllowedMethods
            if (p.returning === false) return newClientAllowedMethods(this.state.referral, !!this.state.schedule.aep, this.openStates)
            return null
        },
        // #727/#730: the open-state rule governs this primary -- allow-listed
        // advisors only, no video. A new client: a non-paying referral in an
        // open state. A returning one: the backend decides from the record.
        openStateRule () {
            if (this.state.primary.returning === true) return this.returningOpenStateOnly
            return this.newOpenState
        },
        // Blocked because of the Benefit Bay wind-down, which has its own message:
        // a returning former client, or a new client whose employer was a partner.
        formerIchraBlock () {
            const p = this.state.primary
            if (p.returning === true) return this.returningFormerIchra
            if (p.returning === false) return !!this.state.referral.groupFormerIchra
            return false
        },
        // #727/#730: the new-client half -- a referral that resolved to
        // non-paying, in an open state. Locks the state chosen in step 1.
        newOpenState () {
            return this.askingClientState && isOpenState(this.state.referral.state, this.openStates)
        },
        // Step 1 is asking for the client's state (a new client whose referral
        // resolved to non-paying -- Other, a non-paying group, Family Member...).
        askingClientState () {
            return this.state.primary.returning === false && showClientState(this.state.referral)
        },
        // #727/#730: the Not eligible card points a non-paying client in a
        // closed state at the open ones.
        notPayingOtherHint () {
            if (!this.askingClientState || !this.state.referral.state || !this.openStates.length) return ''
            return COPY.s1.notPayingOtherHint(stateNames(this.openStates))
        },
        // The referral question is asked on the Other path only (#727).
        referredByLabelText () {
            return this.newOpenState && this.state.referral.source_type === 'OTHER' ? referredByLabel(this.state.referral) : null
        },
        // The pool is already the allow-list once loadAgents asks for it.
        openStateAdvisorNames () {
            return this.openStateRule ? this.agents.map(a => a.first_name) : []
        },
        // Fullserve → show the rest of the form. Empty array → non-paying → block.
        primaryEligible () {
            return Array.isArray(this.allowedMethods) && this.allowedMethods.length > 0
        },
        primaryBlocked () {
            return Array.isArray(this.allowedMethods) && this.allowedMethods.length === 0
        },
        // #679 J-rail: aep copy variant for the SAME blocked card (mockup
        // panel J) — non-aep keeps today's exact strings.
        notPayingHeading () {
            return this.state.schedule.aep ? COPY.s1.notPayingHeadingAep : COPY.s1.notPayingHeading
        },
        notPayingBody () {
            return this.state.schedule.aep ? COPY.s1.notPayingBodyAep : COPY.s1.notPayingBody
        },
        eligible () {
            return eligiblePeople(this.state)
        },
        spouseAdded () {
            return !!(this.spouse && this.state.attendees.some(a => a.record && a.record.id === this.spouse.id))
        },
        // Payer / non-payer routing for phone and video (Dien 2026-10-15): the
        // client's tier. A returning client's comes from /client-allowed-methods/,
        // a new one's from the referral; null means no verdict and no filter.
        advisorPayer () {
            if (this.state.primary.returning === true) return this.returningPayer
            if (this.state.primary.returning === false) return payerFor(this.state.referral)
            return null
        },
        eligibleAgents () {
            return this.agents.filter((a) => {
                if (!a.eligible) return false
                // Phone and video: the advisor offers the method and takes this tier.
                if (this.state.schedule.method === 'phone') return a.agent_phone_consult && agentTakesTier(a, this.advisorPayer)
                if (this.state.schedule.method === 'video') return a.agent_video_consult && agentTakesTier(a, this.advisorPayer)
                if (this.state.schedule.method === 'email') return a.agent_email_consult
                // The survey has its own switch (Dien 2026-10-16: Allison and Haley
                // keep surveys, not email). A payload from before the switch
                // existed falls back to the email one, as it always read.
                if (this.state.schedule.method === 'survey') return a.agent_survey_consult !== undefined ? a.agent_survey_consult : a.agent_email_consult
                return true
            })
        },
        eligibleAgentIds () {
            return this.eligibleAgents.map(a => a.id).join(',')
        },
        agentIds () {
            if (this.pinnedAgent && this.pinnedAgent.id) return [this.pinnedAgent.id]
            if (this.state.schedule.force_agent) return [this.state.schedule.force_agent]
            return this.eligibleAgents.map(a => a.id)
        },
        advisorLabel () {
            if (this.pinnedAgent && this.pinnedAgent.id) return `${this.pinnedAgent.first_name} ${this.pinnedAgent.last_name}`
            const byId = id => this.agents.find(a => a.id === id)
            if (this.state.schedule.force_agent) {
                const a = byId(this.state.schedule.force_agent)
                if (a) return `${a.first_name} ${a.last_name}`
            }
            if (this.state.schedule.time && this.state.schedule.agent) {
                const a = byId(this.state.schedule.agent)
                if (a) return `${a.first_name} ${a.last_name}${COPY.rail.autoAssigned}`
            }
            return COPY.rail.noPreference
        },
        locationSource () {
            // Returning clients prefill their editable fields (incl. ZIP/fips/
            // state) from the CRM record and may correct them, so read the live
            // person; fall back to the record only before the prefill lands.
            const p = this.state.primary
            if (p.returning && !p.fips && !p.state && p.record) return p.record
            return p
        },
        pickedClientIds () {
            const ids = []
            if (this.state.primary.record) ids.push(this.state.primary.record.id)
            this.state.attendees.forEach((a) => { if (a.record) ids.push(a.record.id) })
            return ids
        },
    },
    watch: {
        draftData: {
            deep: true,
            handler () { this.keepDraft() },
        },
        touched (now) {
            if (now) this.keepDraft()
        },
        // Payer / non-payer routing: a defaulted owner the tier (or the pool)
        // rules out for the picked method is dropped, with any slot held under
        // them. An advisor the admin chose on purpose (advisorTouched) stays.
        eligibleAgentIds () {
            const forced = this.state.schedule.force_agent
            if (forced == null || !this.agentDefaulted || this.advisorTouched) return
            if (this.eligibleAgents.some(a => a.id === forced)) return
            if (this.state.schedule.time || this.state.schedule.date) this.releaseHold()
            this.$set(this.state.schedule, 'force_agent', null)
            this.$set(this.state.schedule, 'date', null)
            this.$set(this.state.schedule, 'time', null)
            this.$set(this.state.schedule, 'agent', null)
            this.$set(this.state.schedule, 'agents', null)
            this.agentDefaulted = false
        },
        'locationSource.fips' () { this.loadAgents() },
        // #727: an Other-source client's state is chosen in step 1, before any
        // ZIP exists, and the allow-list pool must load from it alone.
        'locationSource.state' () { this.loadAgents() },
        // #727/#730: a client the open-state rule governs may only be offered the allow-list.
        openStateRule () {
            // #727: the pool is about to change. An advisor auto-defaulted from
            // the old pool (a returning client's owner, picked before the
            // backend said the allow-list applies) must not survive it; an
            // explicit admin choice does, and /book/ judges it.
            if (this.agentDefaulted) {
                this.$set(this.state.schedule, 'force_agent', null)
                this.agentDefaulted = false
            }
            this.loadAgents()
        },
        // #727/#730: the client's state comes from step 1 while the referral is
        // non-paying, and is released again when it stops being asked (the
        // source changes, or a paying group is picked).
        'state.referral.state' (v) {
            // The Other marker is "not an open state", never the client's state.
            if (this.askingClientState) this.$set(this.state.primary, 'state', (v && v !== OTHER_STATE) ? v : null)
        },
        askingClientState (now, was) {
            if (was && !now) {
                this.$set(this.state.referral, 'state', null)
                if (this.state.primary.returning === false) this.$set(this.state.primary, 'state', null)
            }
        },
        // If the client's eligibility narrows the methods (e.g. DPC has no video,
        // or email-only), drop a now-disallowed selection so we never book one.
        allowedMethods (methods) {
            const m = this.state.schedule.method
            if (m && Array.isArray(methods) && !methods.includes(m)) {
                this.releaseHold()
                this.$set(this.state.schedule, 'method', null)
                this.$set(this.state.schedule, 'date', null)
                this.$set(this.state.schedule, 'time', null)
                this.$set(this.state.schedule, 'agent', null)
            }
        },
        // The consultation type (ClientStatusCard's setReason) flips this now —
        // the old standalone Yes/No pill is gone (Dien 2026-09-01). A flip
        // changes the paying-source authority for a RETURNING client, so
        // re-resolve from the backend (a new client's methods are already
        // reactive: the allowedMethods getter calls newClientAllowedMethods
        // with the live aep value). It also changes the appointment type —
        // different duration, different Acuity calendars — so any picked or
        // held slot belongs to the other type and must go (this used to live
        // in MethodTimeCard.setAep's clearSlot).
        'state.schedule.aep' () {
            if (this.state.schedule.time) this.releaseHold()
            this.$set(this.state.schedule, 'date', null)
            this.$set(this.state.schedule, 'time', null)
            this.$set(this.state.schedule, 'agent', null)
            this.$set(this.state.schedule, 'agents', null)
            const p = this.state.primary
            if (p.returning === true && p.record) this.loadAllowedMethods(p.record.id)
        },
        // #679 polish: a NEW client's blocked state is decided entirely by the
        // client-side mirror (newClientAllowedMethods), so no NO_SERVICE_ATTEMPT
        // ever reached the backend — returning clients get theirs logged by
        // /client-allowed-methods/. When the block renders, ping the
        // group-methods endpoint: it resolves the same answer server-side and
        // logs the attempt (source='group_methods'). Once per group+aep per
        // session; best-effort and fire-and-forget.
        primaryBlocked (blocked) {
            if (!blocked) return
            const p = this.state.primary
            if (p.returning !== false) return
            const groupId = this.state.referral.group?.id || this.state.referral.group
            if (!groupId) return
            const key = `${groupId}:${!!this.state.schedule.aep}`
            if (this.noServiceLogged[key]) return
            this.$set(this.noServiceLogged, key, true)
            this.$try('getGroupSchedulingMethods', groupId, { aep: !!this.state.schedule.aep }, { spinner: false, alert: false })
        },
        // Becoming ineligible hides the rail — release any held slot so we don't
        // leave a dangling Redis hold behind.
        primaryEligible (eligible) {
            if (!eligible && this.state.schedule.time) {
                this.releaseHold()
                this.$set(this.state.schedule, 'date', null)
                this.$set(this.state.schedule, 'time', null)
                this.$set(this.state.schedule, 'agent', null)
            }
        },
        preloadedClient: {
            immediate: true,
            handler (client) {
                // The page's fetch() resolves after we mount — consume the
                // preload whenever it lands, but never clobber a flow the
                // admin already started.
                if (client && client.id && !this.state.primary.record) {
                    this.$set(this.state.primary, 'returning', true)
                    this.$set(this.state.primary, 'record', client)
                    fillFromRecord(this.state.primary, client)
                    this.checkUpcoming(client.id)
                    this.loadAllowedMethods(client.id)
                    this.agentDefaulted = false
                    if (client.zip_code) this.autoTimezone(client.zip_code)
                    // The locationSource.fips watcher covers records with a
                    // fips; call directly too so a record carrying only
                    // `state` still loads the advisor pool.
                    this.loadAgents()
                }
            },
        },
        preloadedSpouse: {
            immediate: true,
            handler (s) {
                if (s && !this.spouse) this.spouse = s
            },
        },
    },
    created () {
        this._holdTimer = null
        this.loadOpenStates()
        // A reload (or a back and forward) of a form that was being filled in
        // picks it back up instead of the presets.
        if (this.restoreDraft()) return
        // Method has no default (advisor feedback) but honors ?method= the
        // same way the legacy form did: call→phone, chat→email.
        const raw = ((this.$route && this.$route.query.method) || '').toString().toLowerCase()
        const method = raw === 'call' ? 'phone' : raw === 'chat' ? 'email' : raw
        if (['phone', 'video', 'email'].includes(method)) {
            this.$set(this.state.schedule, 'method', method)
        }
        this.applyQueryPreset()
    },
    mounted () {
        this.loadAgents()
        // The hold is let go on pagehide, when the page really goes: on
        // beforeunload the advisor may still answer the browser's "Leave
        // site?" with Stay, and keep the slot.
        window.addEventListener('beforeunload', this.onBeforeUnload)
        window.addEventListener('pagehide', this.releaseHoldSilently)
        if (window.$root) window.$root.$on('logout', this.onLogout)
    },
    beforeDestroy () {
        clearTimeout(this._holdTimer)
        this.releaseHoldSilently()
        window.removeEventListener('beforeunload', this.onBeforeUnload)
        window.removeEventListener('pagehide', this.releaseHoldSilently)
        if (window.$root) window.$root.$off('logout', this.onLogout)
    },
    methods: {
        draftKey () {
            const user = this.$store.getters.user
            return { userId: (user && user.id) || null, path: (this.$route && this.$route.fullPath) || '' }
        },
        keepDraft () {
            if (!this.touched) return
            const { state, spouse, ...rest } = this.draftData
            saveDraft({ ...this.draftKey(), data: { ...rest, state: draftSchedulerState(state), spouse: draftRecord(spouse) } })
        },
        restoreDraft () {
            const draft = loadDraft(this.draftKey())
            if (!draft || !draft.data || !draft.data.state) return false
            const { state, slotDropped } = restoreSchedulerState(draft.data.state)
            this.state = state
            this.spouse = draft.data.spouse || null
            this.agentDefaulted = !!draft.data.agentDefaulted
            this.advisorTouched = !!draft.data.advisorTouched
            this.touched = true
            this.restoredAt = draft.savedAt
            this.restoredSlot = slotDropped
            // What the page looked up for these people, looked up again.
            const p = this.state.primary
            if (p.returning === true && p.record) {
                this.checkUpcoming(p.record.id)
                this.loadAllowedMethods(p.record.id)
            }
            this.state.attendees.forEach((a) => { if (a.record) this.checkUpcoming(a.record.id) })
            return true
        },
        // Pills, calendar days and slots are buttons: pressing one is input too.
        onFormClick (e) {
            if (e.target && e.target.closest && e.target.closest('button, input, select, textarea, label')) this.touched = true
        },
        onBeforeUnload (e) {
            if (!this.touched) return
            e.preventDefault()
            e.returnValue = ''
        },
        // In-app navigation away (pages/schedule/index.vue beforeRouteLeave).
        async confirmLeave () {
            if (!this.touched) return true
            return !!(await this.$bvModal.msgBoxConfirm(COPY.draft.leaveBody, {
                title: COPY.draft.leaveTitle,
                okTitle: COPY.draft.leaveOk,
                cancelTitle: COPY.draft.leaveCancel,
                footerClass: 'p-2',
                centered: true,
            }))
        },
        dropDraft () {
            clearDraft()
            this.touched = false
        },
        // The draft must not outlive the session it was typed in.
        onLogout () {
            this.dropDraft()
        },
        startOver () {
            this.dropDraft()
            this.reloadPage()
        },
        reloadPage () {
            window.location.reload()
        },
        async onPrimaryPick (row) {
            let resp
            try {
                resp = await this.$api.getClient({ id: row.id })
            } catch (e) {
                this.$alert({ message: (e && e.message) || 'Could not load the client. Please try again.', variant: 'warning' })
                return
            }
            const survey = (resp && resp.survey) || row
            this.$set(this.state.primary, 'record', survey)
            // Prefill the editable on-file fields so ReturningClientFields shows
            // them and any correction rides along to book.py.
            fillFromRecord(this.state.primary, survey)
            this.checkUpcoming(survey.id)
            this.loadAllowedMethods(survey.id)
            this.agentDefaulted = false
            if (survey.zip_code) this.autoTimezone(survey.zip_code)
            const spouseResp = await this.$try('getSpouse', survey.id, { spinner: false, alert: false })
            this.spouse = (spouseResp && !spouseResp.error && spouseResp.spouse) || null
            // Defaults are applied only from loadAgents() success, once the
            // fresh pool for this client has actually arrived.
            this.loadAgents()
        },
        onAttendeePick (row) {
            if (row && row.id) this.checkUpcoming(row.id)
        },
        async loadAllowedMethods (clientId) {
            // Paying-source authority for a returning client. Reset to null
            // (undetermined → form stays collapsed) until the answer lands.
            this.returningAllowedMethods = null
            this.returningEnrolled = null
            this.returningFormerIchra = false
            this.returningOpenStateOnly = false
            this.returningPayer = null
            if (!clientId) return
            // #679: aep must ride along — the rail was never AEP-gated before
            // (client-allowed-methods/ defaulted aep to false server-side).
            const resp = await this.$try('getClientAllowedMethods', clientId, { aep: !!this.state.schedule.aep }, { spinner: false, alert: false })
            if (resp && !resp.error && Array.isArray(resp.allowed_methods)) {
                this.returningAllowedMethods = resp.allowed_methods
                this.returningEnrolled = resp.enrolled === true
                this.returningFormerIchra = resp.former_ichra === true
                this.returningOpenStateOnly = resp.open_state_only === true
                this.returningPayer = typeof resp.payer === 'boolean' ? resp.payer : null
            } else {
                // On a lookup failure, don't hard-block the agent — treat as
                // undetermined so they can still proceed rather than being stuck.
                this.returningAllowedMethods = null
                this.returningEnrolled = null
            }
        },
        async checkUpcoming (clientId) {
            // Best-effort, non-blocking: a slow/failed Acuity lookup must never
            // hold up the scheduler — the notice just doesn't appear. Skip if
            // we've already resolved this client.
            if (!clientId || Object.prototype.hasOwnProperty.call(this.upcomingByClientId, clientId)) return
            let list
            try {
                list = await this.$api.getUpcomingAppointments({ client_ids: [clientId] })
            } catch (e) {
                return
            }
            const arr = Array.isArray(list) ? list : []
            const soonest = arr
                .slice()
                .sort((a, b) => (a.datetime || '').localeCompare(b.datetime || ''))[0] || null
            this.$set(this.upcomingByClientId, clientId, soonest)
        },
        upcomingFor (person) {
            const id = person && person.record && person.record.id
            return id ? (this.upcomingByClientId[id] || null) : null
        },
        onPrimaryReset () {
            this.$set(this.state.primary, 'record', null)
            this.spouse = null
            this.returningAllowedMethods = null
            this.returningEnrolled = null
            this.returningFormerIchra = false
            this.returningOpenStateOnly = false
            this.returningPayer = null
            // Only an auto-applied default gets cleared here — an explicit
            // admin choice (advisorTouched) persists across a client switch.
            if (this.agentDefaulted) {
                this.$set(this.state.schedule, 'force_agent', null)
            }
            this.agentDefaulted = false
        },
        onAdvisorChange () {
            // An explicit choice supersedes any prior auto-default — both for
            // future defaulting (advisorTouched) and for reset bookkeeping
            // (agentDefaulted must not mark the explicit value as auto).
            this.advisorTouched = true
            this.agentDefaulted = false
        },
        applyDefaultAgent () {
            // Default "Specify advisor" to the returning client's owning agent
            // (legacy one-page behavior) — once, only when that agent is in the
            // eligible pool, and never overriding an explicit admin choice.
            if (this.agentDefaulted || this.advisorTouched || this.state.schedule.force_agent != null) return
            const record = this.state.primary.returning && this.state.primary.record
            const id = record && record.agent && record.agent.id
            if (id && this.eligibleAgents.some(a => a.id === id)) {
                if (this.state.schedule.time || this.state.schedule.date) {
                    // Narrowing to the owning agent invalidates a slot held under
                    // the previous pool — release it so summary and booking agree.
                    this.releaseHold()
                    this.$set(this.state.schedule, 'date', null)
                    this.$set(this.state.schedule, 'time', null)
                    this.$set(this.state.schedule, 'agent', null)
                    this.$set(this.state.schedule, 'agents', null)
                }
                this.$set(this.state.schedule, 'force_agent', id)
                this.agentDefaulted = true
            }
        },
        onZip (zip) {
            this.autoTimezone(zip)
            this.loadAgents()
        },
        async autoTimezone (zip) {
            const resp = await this.$try('getTimezoneForZip', zip, { spinner: false, alert: false })
            if (resp && resp.timezone_code) {
                this.$set(this.state.schedule, 'timezone', resp.timezone_code)
            }
        },
        // #727 deep-link / smoke-test presets for a NEW client (a ?id= preload
        // is a returning client and wins):
        //   ?returning=0  ?source=OTHER  ?state=FL
        //   ?ref=client|employer|other (+ ?ref_text=)  ?aep=1|0
        applyQueryPreset () {
            const q = (this.$route && this.$route.query) || {}
            const str = (v) => { const x = Array.isArray(v) ? v[0] : v; return x == null ? '' : String(x).trim() }
            const aep = str(q.aep).toLowerCase()
            if (['1', 'true', 'yes'].includes(aep)) this.$set(this.state.schedule, 'aep', true)
            else if (['0', 'false', 'no'].includes(aep)) this.$set(this.state.schedule, 'aep', false)
            if (this.preloadedClient) return
            const returning = str(q.returning).toLowerCase()
            if (['0', 'false', 'no', 'new'].includes(returning)) this.$set(this.state.primary, 'returning', false)
            const source = str(q.source).toUpperCase()
            if (source) {
                this.$set(this.state.primary, 'returning', false)
                this.$set(this.state.referral, 'source_type', source)
            }
            if (this.state.referral.source_type !== 'OTHER') return
            const state = str(q.state).toUpperCase()
            if (state) this.$set(this.state.referral, 'state', state)
            const ref = str(q.ref).toUpperCase()
            if (['CLIENT', 'EMPLOYER', 'OTHER'].includes(ref)) {
                this.$set(this.state.referral, 'referred_by_type', ref)
                const text = str(q.ref_text)
                if (text) {
                    const key = ref === 'CLIENT' ? 'referred_by_client_name' : ref === 'EMPLOYER' ? 'referred_by_employer_name' : 'referred_by_note'
                    this.$set(this.state.referral, key, text)
                }
            }
        },
        // #727. A failed fetch returns [] and must never clear a list we
        // already have, so only a non-empty answer replaces it.
        async loadOpenStates () {
            const states = await fetchOpenStates(this.$api)
            if (states.length) this.openStates = states
        },
        async loadAgents () {
            const src = this.locationSource
            if (!src.fips && !src.state) return
            try {
                const resp = await this.$api.getAgentsForLocation({
                    fips: src.fips,
                    state: src.state,
                    open_state_rule: this.openStateRule || undefined,
                })
                this.agents = (resp && resp.agents) || []
                this.applyDefaultAgent()
            } catch (e) {
                this.$alert({ message: (e && e.message) || 'Could not load eligible advisors.', variant: 'warning' })
            }
        },
        addAttendee () {
            this.state.attendees.push(newPerson())
        },
        removeAttendee (i) {
            this.state.attendees.splice(i, 1)
        },
        addSpouse () {
            if (!this.spouse || this.spouseAdded) return
            const a = newPerson()
            // On-file is a fact (they ARE an existing account); guidance is a
            // choice — it stays unanswered so the admin must ask (2026-07-19
            // advisor feedback; the attendee_status gate blocks until answered).
            this.$set(a, 'returning', true)
            this.$set(a, 'record', this.spouse)
            this.$set(a, 'first_name', this.spouse.first_name || '')
            this.$set(a, 'last_name', this.spouse.last_name || '')
            this.$set(a, 'dob', this.spouse.dob || null)
            this.$set(a, 'gender', this.spouse.gender || null)
            this.$set(a, 'email', this.spouse.email || '')
            this.$set(a, 'relationship', 'SPOUSE')
            // Prefill editable on-file fields (phone, ZIP/county, address,
            // parts) so ReturningClientFields shows them once guidance is Yes.
            // Coverage date is deliberately left blank — see fillFromRecord.
            fillFromRecord(a, this.spouse)
            this.checkUpcoming(this.spouse.id)
            this.state.attendees.push(a)
            const name = `${this.spouse.first_name || ''} ${this.spouse.last_name || ''}`.trim()
            this.$alert({ message: COPY.s3.spouseToast(name), variant: 'success' })
        },
        // --- slot holds, V2-style (StepByStepV2.placeHold/releaseHold) ---
        async onPickSlot (slot) {
            await this.releaseHold()
            this.$set(this.state.schedule, 'time', slot.time)
            this.$set(this.state.schedule, 'agent', slot.agent)
            this.$set(this.state.schedule, 'agents', slot.agents)
            try {
                const resp = await this.$api.$post('/api/scheduling/holds/', {
                    agent_id: slot.agent,
                    time: slot.time,
                    session_id: this.sessionId,
                })
                this.heldUntil = (resp.expires_at || 0) * 1000
                this.scheduleHoldTimer()
            } catch (err) {
                this.$set(this.state.schedule, 'time', null)
                this.$alert({ message: (err && err.message) || 'That time was just taken. Please pick a different one.', variant: 'warning' })
            }
        },
        scheduleHoldTimer () {
            clearTimeout(this._holdTimer)
            if (!this.heldUntil) return
            this._holdTimer = setTimeout(() => this.onHoldExpired(), Math.max(this.heldUntil - Date.now(), 0))
        },
        async onHoldExpired () {
            // Silently re-acquire the same slot (V2 parity) — only bounce the
            // admin if someone else grabbed it while the hold lapsed.
            this.heldUntil = null
            const { time, agent } = this.state.schedule
            if (!time || !agent) return
            try {
                const resp = await this.$api.$post('/api/scheduling/holds/', {
                    agent_id: agent,
                    time,
                    session_id: this.sessionId,
                })
                this.heldUntil = (resp.expires_at || 0) * 1000
                this.scheduleHoldTimer()
            } catch (err) {
                this.$set(this.state.schedule, 'time', null)
                this.$set(this.state.schedule, 'agent', null)
                this.$alert({ message: 'Someone just grabbed that time. Please pick another.', variant: 'warning' })
                if (this.$refs.methodTime) this.$refs.methodTime.reloadTimes()
            }
        },
        async releaseHold () {
            clearTimeout(this._holdTimer)
            const { time, agent } = this.state.schedule
            this.heldUntil = null
            if (!time || !agent) return
            try {
                await this.$api.$post('/api/scheduling/holds/release/', {
                    agent_id: agent,
                    time,
                    session_id: this.sessionId,
                })
            } catch (e) { /* best-effort */ }
        },
        releaseHoldSilently () {
            // On the way out of the page: a keepalive fetch with the CSRF token,
            // the beacon as fallback (util/hold-release.js). The old beacon-only
            // release was refused by CSRF in production and the hold outlived
            // the page by 15 minutes (2026-10-06).
            const { time, agent } = this.state.schedule
            if (!time || !agent) return
            if (!releaseHoldOnLeave({ agent, time, sessionId: this.sessionId })) this.releaseHold()
        },
        onBlocked (gates) {
            gates.forEach((g) => {
                if (g.key === 'client_sc') this.$set(this.state.primary, 'scAttempted', true)
                if (g.key === 'attendee_sc') {
                    this.state.attendees.forEach((a) => {
                        if (a.guidance && !a.returning) this.$set(a, 'scAttempted', true)
                    })
                }
                if (g.key === 'soa_pending') {
                    this.eligible.forEach((p) => {
                        if (!p.soa.captured && !p.soa.absent) this.$set(p.soa, 'attempted', true)
                    })
                }
            })
            this.$alert({ message: gates[0].message, variant: 'warning' })
        },
        emailAgentId () {
            if (this.pinnedAgent && this.pinnedAgent.id) return this.pinnedAgent.id
            const owning = this.state.primary.record && this.state.primary.record.agent
            // #727: when the open-state rule governs, the owner is used only if
            // they are in the (allow-listed) pool -- /book/ refuses anyone else.
            const owner = owning && owning.id &&
                (!this.openStateRule || this.eligibleAgents.some(a => a.id === owning.id))
                ? owning.id
                : null
            return this.state.schedule.force_agent ||
                owner ||
                (this.eligibleAgents.length ? this.eligibleAgents[Math.floor(Math.random() * this.eligibleAgents.length)].id : null)
        },
        async book () {
            this.booking = true
            try {
                // Email and survey are both async (no held slot) — same
                // email-schedulable agent pick as before (#679).
                const asyncMode = this.state.schedule.method === 'email' || this.state.schedule.method === 'survey'
                const body = buildBookBody(this.state, {
                    agentId: asyncMode ? this.emailAgentId() : null,
                    sessionId: this.sessionId,
                    openStates: this.openStates,
                })
                const resp = await this.$try('bookSchedulingTime', body)
                if (resp && resp.error) {
                    // Likely a just-taken slot: drop the stale pick and refresh
                    // the day's times so the admin re-picks from live data.
                    if (!asyncMode && this.state.schedule.time) {
                        await this.releaseHold()
                        this.$set(this.state.schedule, 'time', null)
                        this.$set(this.state.schedule, 'agent', null)
                        if (this.$refs.methodTime) this.$refs.methodTime.reloadTimes()
                    }
                    return
                }
                this.$alert({ message: 'Appointment Booked', variant: 'success' })
                this.dropDraft()
                const id = resp && resp.client_ids && resp.client_ids[0]
                if (id) this.$router.push(`/survey/${id}`)
            } finally {
                this.booking = false
            }
        },
    },
}
